import { useState } from "react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/Bits";
import { AlertTriangle } from "lucide-react";

export default function AppRisk() {
  const [f, setF] = useState({ account_size: 100000, risk_pct: 1, entry: 67000, stop_loss: 65500, tp: 70000 });
  const [res, setRes] = useState(null);

  const calc = async () => {
    const body = { ...f, account_size: +f.account_size, risk_pct: +f.risk_pct, entry: +f.entry, stop_loss: +f.stop_loss, tp: f.tp ? +f.tp : null };
    const { data } = await api.post("/risk/calculate", body);
    setRes(data);
  };

  return (
    <DashboardLayout title="Risk Manager">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="font-display text-lg font-semibold">Position Size Calculator</h3>
          <p className="mt-1 text-sm text-muted-foreground">Work out how large a position should be for a fixed risk amount.</p>
          <div className="mt-5 space-y-4">
            <Inp label="Account size ($)" value={f.account_size} onChange={(v) => setF({ ...f, account_size: v })} testid="risk-account" />
            <div>
              <label className="mb-1.5 block text-sm font-medium">Risk per trade</label>
              <div className="grid grid-cols-4 gap-2">
                {[0.25, 0.5, 1, 2].map((r) => <button key={r} onClick={() => setF({ ...f, risk_pct: r })} data-testid={`risk-pct-${r}`} className={`rounded-lg py-2 text-sm ${+f.risk_pct === r ? "bg-primary text-primary-foreground" : "border border-border"}`}>{r}%</button>)}
              </div>
            </div>
            <Inp label="Entry price" value={f.entry} onChange={(v) => setF({ ...f, entry: v })} testid="risk-entry" />
            <Inp label="Stop loss" value={f.stop_loss} onChange={(v) => setF({ ...f, stop_loss: v })} testid="risk-sl" />
            <Inp label="Take profit (optional)" value={f.tp} onChange={(v) => setF({ ...f, tp: v })} testid="risk-tp" />
            <button onClick={calc} data-testid="risk-calc" className="w-full rounded-xl bg-primary py-2.5 font-semibold text-primary-foreground">Calculate</button>
          </div>
        </div>

        <div className="space-y-4">
          {res ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Risk Amount" value={`$${fmt(res.risk_amount)}`} testid="risk-amount" />
                <StatCard label="Position Size" value={fmt(res.position_size, 4)} testid="risk-size" />
                <StatCard label="Position Value" value={`$${fmt(res.position_value)}`} />
                <StatCard label="Exposure" value={`${res.exposure_pct}%`} accent={res.exposure_pct > 100 ? "text-warning" : ""} />
                <StatCard label="Stop Distance" value={fmt(res.stop_distance, 4)} />
                <StatCard label="Risk / Reward" value={res.risk_reward ?? "—"} />
              </div>
              {res.warnings?.map((w, i) => (
                <div key={i} className="flex gap-2 rounded-xl border border-warning/30 bg-warning/5 p-3 text-sm text-warning" data-testid="risk-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0" /> {w}
                </div>
              ))}
            </>
          ) : (
            <div className="flex h-64 items-center justify-center rounded-2xl border border-dashed border-border text-muted-foreground">Enter your parameters and calculate.</div>
          )}
          <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
            <h4 className="font-display text-base font-semibold text-foreground">Guidance</h4>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Risking more than 2% per trade is aggressive and can compound losses quickly.</li>
              <li>Size positions from your stop distance, not from a round lot.</li>
              <li>Keep total portfolio exposure within limits you've defined in advance.</li>
            </ul>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Inp({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium">{label}</label>
      <input type="number" step="any" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full rounded-xl border border-border bg-card px-3.5 py-2.5 font-mono text-sm outline-none focus:border-primary" />
    </div>
  );
}
